import React, {ReactNode} from 'react';
import _ from 'underscore';

import {StringIDFolder} from 'constants/FolderConstants';
import {User} from 'constants/UserConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import FinderActions from 'file-browser/FinderActions';
import FinderUtil from 'file-browser/FinderUtil';
import {FileSummary} from 'folder-items/FileConstants';
import {ListableItem} from 'search/SearchConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ShortcutIcon from 'ui-library/icons/ShortcutIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal from 'ui-library/modals/Modal';
import SortableTable from 'ui-library/SortableTable';
import * as ShortcutUtil from 'util/ShortcutUtil';

interface FileProperties {
  id: string | number;
  name: string;
  lowercaseName: string;
  creatorHandle: string;
  projectName: string;
  lowercaseProjectName: string;
  isShortcut: boolean;
}

interface FilesForDeleteModalProps {
  onRequestClose: () => void;
  fileIds: Array<string>;
  doDelete: () => void;
  /* e.g., '<span>the project <strong>Foo</strong></span>' or 'these folders' */
  deleteDescription?: ReactNode;
  /* Whether to delete shortcuts (if available) instead of original files */
  shouldDetectShortcuts: boolean;
}

interface FilesForDeleteModalState {
  filesForDelete: Array<FileProperties> | null;
  foldersForDelete: Array<FileProperties> | null;
  requestFailed: boolean;
  userMessage: string;
}

class FilesForDeleteModal extends React.Component<FilesForDeleteModalProps, FilesForDeleteModalState> {
  static defaultProps = {shouldDetectShortcuts: false};

  static open(props: Omit<FilesForDeleteModalProps, 'onRequestClose' | 'shouldDetectShortcuts'>): void {
    // eslint-disable-next-line no-restricted-syntax
    const modalProps = _.extend({}, props, {
      onRequestClose: () => {
        LegacyModalContainer.close(FilesForDeleteModal);
      },
    });
    LegacyModalContainer.open(FilesForDeleteModal, React.createElement(FilesForDeleteModal, modalProps));
  }

  static openForFileDelete(
    props: Pick<FilesForDeleteModalProps, 'deleteDescription'>,
    files: Array<ListableItem>,
    finderId: string
  ): void {
    // eslint-disable-next-line no-restricted-syntax
    const modalProps = _.extend({}, props, {
      onRequestClose: () => {
        LegacyModalContainer.close(FilesForDeleteModal);
      },
      fileIds: files.map((file) => file.id),
      doDelete: () => {
        // @ts-expect-error I think the typing on FinderUtil.parentFolderId is probably a bit janky and that
        // it will work just fine with a `ListableItem`
        const parentFolderIds = files.map((file) => FinderUtil.parentFolderId(file)!);
        FinderActions.bulkDelete(finderId, files, {parentFolderIds});
      },
      shouldDetectShortcuts: ShortcutUtil.shouldDetectShortcuts(finderId),
    });
    LegacyModalContainer.open(FilesForDeleteModal, React.createElement(FilesForDeleteModal, modalProps));
  }

  override state: FilesForDeleteModalState = {
    // filesForDelete and foldersForDelete are null when loading or when an error has occurred
    filesForDelete: null,
    foldersForDelete: null,
    requestFailed: false,
    userMessage: '',
  };

  override componentDidMount(): void {
    FileBrowserDAO.getFilesForDelete(this.props.fileIds, this.props.shouldDetectShortcuts)
      .done(
        (response: {
          filesForDelete: Array<
            Pick<FileSummary, 'name' | 'id' | 'projectName'> & {isShortcut: boolean; creator: User}
          >;
          foldersForDelete: Array<StringIDFolder>;
        }) => {
          this.setState({
            filesForDelete: response.filesForDelete.map((file) => this._getFileProperties(file)),
            foldersForDelete: response.foldersForDelete.map((file) => this._getFileProperties(file)),
          });
        }
      )
      .fail((err) => {
        let message =
          'Sorry, something went wrong and your delete cannot be completed.' +
          'Try deleting your items again, or contact support for assistance';
        if (err?.responseJSON?.userMessage) {
          message = `${err.responseJSON.userMessage} Please fix the issue, or contact support for assistance.`;
        }
        this.setState({requestFailed: true, userMessage: message});
      });
  }

  _hasLoaded = (): boolean => this.state.filesForDelete != null && this.state.foldersForDelete != null;

  _getFileProperties = (
    file:
      | (Pick<FileSummary, 'name' | 'id' | 'projectName'> & {isShortcut: boolean; creator: User})
      | StringIDFolder
  ): FileProperties => ({
    id: file.id,
    name: file.name,
    lowercaseName: file.name.toLowerCase(),
    // @ts-expect-error no extant discriminators for FileSummary vs FolderSummary... neither of which have `creator` anyways
    creatorHandle: file.creator ? file.creator.handle : '',
    // @ts-expect-error no extant discriminators for FileSummary vs FolderSummary
    projectName: file.projectName,
    // @ts-expect-error no extant discriminators for FileSummary vs FolderSummary
    lowercaseProjectName: file.projectName.toLowerCase(),
    // @ts-expect-error no extant discriminators for FileSummary vs FolderSummary
    isShortcut: file.isShortcut,
  });

  // Sort all folders before all files, and within each category sort the hidden items
  // (the items that were not directly selected for delete) last.
  _getSortedFileList = (): Array<FileProperties> => {
    // eslint-disable-next-line no-restricted-syntax
    const isHidden = (f: FileProperties): boolean => !_.contains(this.props.fileIds, f.id);
    const folders = _.sortBy(this.state.foldersForDelete!, isHidden);
    const files = _.sortBy(this.state.filesForDelete!, isHidden);
    return folders.concat(files);
  };

  _handleConfirmDelete = (): void => {
    this.props.doDelete();
    this.props.onRequestClose();
  };

  renderFilesForDelete = (): ReactNode => {
    if (this.state.requestFailed) {
      return this.state.userMessage;
    } else if (!this._hasLoaded()) {
      return (
        <div className="text-center u-spacing-top u-spacing-bottom">
          <HorizontalThrobber />
        </div>
      );
    } else {
      return (
        <div>
          <p>
            Are you sure you want to delete {this.props.deleteDescription}? This action is not reversible.
          </p>
          <p>The following items and folders will be deleted:</p>
          <div className="filesForDeleteModal-table">
            <SortableTable
              columns={[
                SortableTable.defaultColumn('Type', 'id', {
                  render: (_column, row: FileProperties, _props) => {
                    const IconClass = row.isShortcut ? ShortcutIcon : BenchlingIcon;
                    // @ts-expect-error we are apparently only getting StringIDFolders but the return types
                    // on the DAO are too broad
                    return <IconClass className="faded" icon={FileBrowserUtil.getFileIconName(row)} />;
                  },
                }),
                SortableTable.defaultColumn('Name', 'lowercaseName', {
                  render: (_column, row: FileProperties, _props) => row.name,
                }),
                SortableTable.defaultColumn('Project', 'lowercaseProjectName', {
                  render: (_column, row: FileProperties, _props) => row.projectName,
                }),
                SortableTable.defaultColumn('Creator', 'creatorHandle'),
              ]}
              rows={this._getSortedFileList()}
              clickable={false}
              selectable={false}
              hoverable={true}
              coloredHeader={true}
              wrapCell={true}
            />
          </div>
        </div>
      );
    }
  };

  override render(): JSX.Element {
    return (
      <Modal
        title="Confirm delete"
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            text: 'Permanently delete',
            onClick: this._handleConfirmDelete,
            disabled: !this._hasLoaded(),
            style: ButtonStyle.NEGATIVE,
          },
        ]}
      >
        {this.renderFilesForDelete()}
      </Modal>
    );
  }
}

export default FilesForDeleteModal;
